<template>
    <ModalWrapper v-model="isShowModal">
        <template v-slot:title>
            {{ $t('edit_companies_profiles') }}
        </template>
        <template v-slot:content>
            <v-form :disabled="loading" data-cy="edit-companies-form">
                <v-row dense>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            v-model="formData.managerId"
                            item-value="personId"
                            :items="users"
                            item-title="name"
                            :label="$t('manager')"
                            :loading="loading"
                            :disabled="loading"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="manager-id"
                        />
                    </v-col>
                    <v-col v-if="!authStore.isAgency" cols="12" sm="6">
                        <v-select
                            v-model="formData.groupId"
                            :items="contractGroups"
                            item-title="name"
                            item-value="id"
                            :label="$t('group_of_contracts')"
                            :loading="loading"
                            :disabled="loading"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="contract-groups"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </template>
        <template v-slot:btns>
            <v-btn variant="flat" color="primary" :disabled="loading" :loading="loading" data-cy="save" @click="send">
                {{ $t('save') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>

<script setup lang="ts">
    import type {ContractGroupsType} from '~/utils/types/account/companies/types'
    import {clean, clone} from '~/utils/helpers'

    const initFormData = () => ({
        managerId: null,
        groupId: null,
    })

    const props = defineProps<{
        companies: number[]
    }>()

    const emit = defineEmits<{
        (e: 'sent'): void
    }>()

    const isShowModal = defineModel<boolean>()

    const formData = ref(initFormData())
    const loading = ref<boolean>(false)
    const users = ref<any[]>([])
    const contractGroups = ref<ContractGroupsType[]>([])

    const app = useNuxtApp()
    const {t} = useI18n()

    watch(isShowModal, async () => {
        if (isShowModal.value) formData.value = initFormData()
    })

    onMounted(async () => {
        try {
            loading.value = true
            users.value = (await app.$api.persons.get({companyId: authStore.company.id})).users
                .map(({personId, firstName, lastName}) => ({
                    personId,
                    name: `${lastName || ''} ${firstName || ''}`.trim(),
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
            contractGroups.value = (await app.$api.contractGroups.get()).contractGroups
        } catch (e) {
            users.value = []
        } finally {
            loading.value = false
        }
    })

    const send = async (): Promise<void> => {
        try {
            loading.value = true
            const data = clone(formData.value)
            clean(data)
            await app.$api.companiesMassEdit.post({
                ids: props.companies,
                ...data,
            })
            app.$toast.success(t('success_message.saved_successfully'))
            emit('sent')
            isShowModal.value = false
        } catch (e) {
            app.$toast.error(t('data_update_fail'))
        } finally {
            loading.value = false
        }
    }
</script>
